import { useState, useEffect } from 'react';
import { Button, Select, TextInput, toast } from '@etherisc/ui-kit';
import type { Organization } from '../../../types/organization';
import type { Farmer } from '../../../types/farmer';
import { organizationService } from '../../../services/organizationService';
import { farmerService } from '../../../services/farmerService';
import { quoteService } from '../../../services/quoteService';
import { ApiError } from '../../../utils/errorHandling';
import { BulkUploadModal } from '../../common/BulkUploadModal';
import { apiClient } from '../../../services/api';
import {
  UsersIcon,
  BuildingIcon,
  UploadIcon,
  InfoIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  EyeIcon,
  TrashIcon,
  SearchIcon,
} from 'lucide-react';
import { WizardStepProps } from '../../../framework/wizard';
import type { QuoteWizardData } from '../QuoteWizard';
import { DocumentPreviewModal } from '../../common/DocumentPreviewModal';
import { createCompleteQuote } from '../utils/quoteHelpers';
import { getAdministrativeUnitByCode } from '../../../services/administrativeUnitService';
import { getCountryConfig } from '../../../config/countries';

// Helper function to safely format coordinates
const formatCoordinate = (coord: number | string | null | undefined): string => {
  if (coord === null || coord === undefined) return 'N/A';
  const numCoord = Number(coord);
  if (isNaN(numCoord)) return 'Invalid';
  return numCoord.toFixed(4);
};

// Helper function to resolve administrative level codes to names
const resolveAdminLevelNames = async (
  adminLevels: Record<string, string> | undefined
): Promise<Record<string, string>> => {
  if (!adminLevels || typeof adminLevels !== 'object') {
    return {};
  }

  const resolved: Record<string, string> = {};
  
  // Resolve levels 1-3 (Province, District, Ward) from codes
  for (const [level, code] of Object.entries(adminLevels)) {
    if (!code) {
      resolved[level] = '';
      continue;
    }
    
    // Level 4 (Village) is freetext, so use as-is
    if (level === '4') {
      resolved[level] = code;
      continue;
    }
    
    // Resolve levels 1-3 from database
    try {
      const unit = await getAdministrativeUnitByCode(code);
      resolved[level] = unit?.adminName || code;
    } catch (error) {
      console.error(`Error resolving admin code ${code}:`, error);
      resolved[level] = code; // Fallback to code
    }
  }
  
  return resolved;
};

export function ParticipantsStepNew({ 
  data, 
  updateData, 
  errors 
}: WizardStepProps<QuoteWizardData>) {
  
  // Note: Using shared createCompleteQuote utility to avoid code duplication
  
  // State for proof of insurable interest confirmation
  const [proofConfirmed, setProofConfirmed] = useState(data.proofOfInterestConfirmed || false);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<Array<{id: string; filename: string; url: string; size?: number; mimeType?: string}>>(
    data.proofOfInterestDocuments || []
  );
  const [previewDoc, setPreviewDoc] = useState<{url: string; filename: string; size?: number; mimeType?: string} | null>(null);
  
  // Helper functions to extract data from single source of truth (quote.participants)
  const getPolicyholder = () => {
    return data.quote?.participants?.find(p => p.role === 'POLICYHOLDER' && p.farmer) || null;
  };
  
  const getCurrentSumInsured = () => {
    if (getPolicyType() === 'individual') {
      const sumInsured = getPolicyholder()?.sumInsured;
      return sumInsured !== undefined ? sumInsured : undefined;
    } else {
      // For master policies, sum from all participants
      return data.quote?.participants?.reduce((total, p) => total + (Number(p.sumInsured) || 0), 0) || 0;
    }
  };

  const getCurrentHectaresAnimalCount = () => {
    if (getPolicyType() === 'individual') {
      const hectaresAnimalCount = getPolicyholder()?.hectaresAnimalCount;
      return hectaresAnimalCount !== undefined ? hectaresAnimalCount : undefined;
    } else {
      // For master policies, sum from all participants
      return data.quote?.participants?.reduce((total, p) => total + (Number(p.hectaresAnimalCount) || 0), 0) || 0;
    }
  };
  
  const getSelectedOrganization = () => {
    const orgParticipant = data.quote?.participants?.find(p => p.role === 'POLICYHOLDER' && p.organizationId);
    return orgParticipant?.organization || null;
  };
  
  const getPolicyType = () => {
    return data.quote?.isMasterPolicy ? 'master' : 'individual';
  };
  
  const [activeTab, setActiveTab] = useState<'farmers' | 'organizations'>(
    getPolicyType() === 'master' ? 'organizations' : 'farmers'
  );
  const [farmers, setFarmers] = useState<Farmer[]>([]);
  const [farmerSearchQuery, setFarmerSearchQuery] = useState('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [dataError, setDataError] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Filter farmers based on search query (name, farmer code, phone)
  const filteredFarmers = farmerSearchQuery.trim()
    ? farmers.filter(f => {
        const q = farmerSearchQuery.toLowerCase();
        return (
          f.name?.toLowerCase().includes(q) ||
          f.farmerCode?.toLowerCase().includes(q) ||
          f.phone?.toLowerCase().includes(q)
        );
      })
    : farmers;

  // Get specific errors for this step from the framework's errors array
  const policyholderError = errors.find(e => e.includes('policyholder'));
  const organizationError = errors.find(e => e.includes('organization'));

  // Load farmers and organizations
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        setDataError(null);

        // Load farmers and organizations in parallel
        const [farmersResponse, orgsResponse] = await Promise.all([
          farmerService.getFarmers({ limit: 100, viewAllOrgs: false }),
          organizationService.getOrganizationsForSelection(),
        ]);

        setFarmers(farmersResponse.farmers.filter(f => f.isActive));
        setOrganizations(orgsResponse.organizations);
      } catch (error) {
        console.error('Error loading data:', error);
        setDataError(error instanceof ApiError ? error.message : 'Failed to load data');
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
  }, []);

  // REMOVED: Tab sync effect that was fighting user tab clicks
  // Tabs are now pure UI navigation - they don't need to sync with policy type
  // Policy type is determined by actual data selections, not tab position

  // Handle tab switching (pure UI navigation - no policy type changes)
  const handleTabChange = (newTab: 'farmers' | 'organizations') => {
    setActiveTab(newTab);
    // NO policy type changes - tabs are just UI navigation
    // Policy type only changes when user makes actual selections (farmer/org/upload)
  };

  // Note: Removed sumInsured sync useEffect - now using quote object directly as single source of truth

  const handleFarmerSelection = (farmer: Farmer) => {
    // Create new participant for quote.participants (single source of truth)
    const newParticipant = {
      id: `temp-${Date.now()}`, // Temporary ID until saved to backend
      quoteId: data.quote?.id || '',
      farmerId: farmer.id,
      organizationId: undefined,
      role: 'POLICYHOLDER' as const,
      isActive: true,
      createdAt: new Date(),
      bulkUploadId: undefined,
      bulkUploadRowIndex: undefined,
      paymentDetails: undefined,
      sumInsured: undefined, // Let user enter this value
      farmer: farmer
    };
    
    // Update quote.participants directly (single source of truth)
    const updatedParticipants = [newParticipant]; // Individual policy has only one participant
    
    // ACTUAL DATA CHANGE: User selected a farmer - switch to individual policy
    updateData({
      quote: createCompleteQuote(data, {
        isMasterPolicy: false,
        participants: updatedParticipants
      }),
      // Clear master policy data when switching to individual
      bulkUploadData: undefined
    });
  };

  const handleOrganizationSelection = async (organizationId: string) => {
    console.log('🔍 ORGANIZATION SELECTION:', {
      organizationId,
      currentParticipants: data.quote?.participants?.length || 0,
      switchingFromIndividual: getPolicyType() === 'individual'
    });
    
    const organization = organizations.find(org => org.id === organizationId);
    
    // EXPECTED PROCESS: Clear participants, then add organization as POLICYHOLDER
    const organizationParticipant = {
      id: `org-${Date.now()}`, // Temporary ID until saved to backend
      quoteId: data.quote?.id || '',
      farmerId: undefined,
      organizationId: organizationId,
      role: 'POLICYHOLDER' as const,
      isActive: true,
      createdAt: new Date(),
      bulkUploadId: undefined,
      bulkUploadRowIndex: undefined,
      paymentDetails: undefined,
      sumInsured: undefined, // Organizations don't have sum insured
      farmer: undefined,
      organization: organization
    };
    
    const masterPolicyQuote = createCompleteQuote(data, {
      participants: [organizationParticipant], // Organization as POLICYHOLDER
      isMasterPolicy: true,
      // Clear organization-dependent data when changing organization
      quotationResults: undefined,
      quotationJobId: undefined,
      protectionLevels: undefined,
      selectedScenarios: undefined,
      locationValidation: undefined,
      // Keep product, coverage dates, perils as they're not organization-specific
    });
    
    console.log('🔍 MASTER POLICY SETUP:', {
      beforeParticipants: data.quote?.participants?.length || 0,
      afterParticipants: masterPolicyQuote.participants.length,
      organizationParticipant: organizationParticipant
    });
    
    // ACTUAL DATA CHANGE: User selected an organization - switch to master policy
    // This is a major change that affects calculations and subsequent steps
    updateData({
      // Remove policyType - now in quote.isMasterPolicy
      quote: masterPolicyQuote,
      // Clear bulk upload data when changing organization
      bulkUploadData: undefined,
      // Explicitly set recalc needed since organization change affects calculations
      recalcNeeded: true
    });
    
    // Update quote to master policy and save organization participant to database
    if (data.quote?.id) {
      try {
        await quoteService.updateQuote(data.quote.id, {
          isMasterPolicy: true,
          participants: [
            {
              organizationId: organizationId,
              role: 'POLICYHOLDER'
              // No farmerId for organization participants
              // No sumInsured field for organizations
            } as { organizationId: string; role: 'POLICYHOLDER' }
          ]
        });
        console.log('🔍 SAVED ORGANIZATION TO DATABASE:', organizationId);
      } catch (error) {
        console.error('Failed to update quote to master policy:', error);
      }
    }
  };

  const selectedOrganization = getSelectedOrganization();

  if (loadingData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading participants...</span>
      </div>
    );
  }

  if (dataError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <AlertCircleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Data</h3>
            <p className="mt-2 text-sm text-red-700">{dataError}</p>
          </div>
        </div>
      </div>
    );
  }

  // Check if quote is approved (read-only mode)
  const isReadOnly = data.quote?.state === 'approved';

  return (
    <div className={`space-y-6 ${isReadOnly ? 'pointer-events-none opacity-60' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UsersIcon className="h-5 w-5 text-blue-600" />
          <h4 className="text-lg font-medium text-gray-900">Participants & Policy Type</h4>
        </div>
        {isReadOnly && (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircleIcon className="h-5 w-5" />
            <span className="text-sm font-medium">Approved</span>
          </div>
        )}
      </div>

      {/* Note: Removed policy type instructions - validation now handled by standard error card */}

      {/* Tab Interface */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Tab Headers */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          <button
            onClick={() => handleTabChange('farmers')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'farmers'
                ? 'bg-white border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <UsersIcon className="h-4 w-4" />
              Farmers (Individual Policy)
            </div>
          </button>
          
          <button
            onClick={() => handleTabChange('organizations')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'organizations'
                ? 'bg-white border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <BuildingIcon className="h-4 w-4" />
              Organizations (Master Policy)
            </div>
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'farmers' ? (
            <div className="space-y-4">
              <h5 className="font-medium text-gray-900">Select Farmer (Individual Policy)</h5>
              <p className="text-sm text-gray-600">
                Choose the farmer who will be the policyholder for this individual insurance policy.
              </p>

              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
                <div className="pl-10">
                  <TextInput
                    placeholder="Search by name, farmer code, or phone..."
                    value={farmerSearchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFarmerSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <Select
                label="Policyholder *"
                placeholder="Select a farmer..."
                options={filteredFarmers.map(farmer => ({
                  value: farmer.id,
                  label: `${farmer.name} (${farmer.farmerCode})`,
                }))}
                value={getPolicyholder()?.farmerId || ''}
                onValueChange={(farmerId) => {
                  const farmer = farmers.find(f => f.id === farmerId);
                  if (farmer) {
                    handleFarmerSelection(farmer);
                  }
                }}
                error={policyholderError}
              />

              {/* Selected Farmer Details */}
              {getPolicyholder()?.farmer && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    <h6 className="font-medium text-green-900">Selected Policyholder</h6>
                  </div>
                  <div className="text-sm text-green-800">
                    <p><strong>Name:</strong> {getPolicyholder()?.farmer?.name}</p>
                    <p><strong>Code:</strong> {getPolicyholder()?.farmer?.farmerCode}</p>
                    <p><strong>Phone:</strong> {getPolicyholder()?.farmer?.phone}</p>
                    {getPolicyholder()?.farmer?.county && (
                      <p><strong>County:</strong> {getPolicyholder()?.farmer?.county}</p>
                    )}
                    {/* Location information */}
                    {getPolicyholder()?.farmer?.latitude && getPolicyholder()?.farmer?.longitude && (
                      <p><strong>Location:</strong> {formatCoordinate(getPolicyholder()?.farmer?.latitude || 0)}, {formatCoordinate(getPolicyholder()?.farmer?.longitude || 0)}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Sum Insured and Hectares/Animals Insured for Individual Policy */}
              {getPolicyholder() && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Sum Insured */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Sum Insured (USD)
                    </label>
                    <TextInput
                      placeholder="Enter sum insured amount (e.g., 25000)"
                      value={getCurrentSumInsured() != null ? getCurrentSumInsured()!.toString() : ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value;
                        
                        // Allow empty input - set to undefined to avoid validation error
                        if (value === '') {
                          const updatedParticipants = data.quote?.participants?.map(p => 
                            p.role === 'POLICYHOLDER' ? { ...p, sumInsured: undefined } : p
                          ) || [];
                          
                          // Update only the specific fields that changed
                          const updatedQuote = createCompleteQuote(data, {
                            participants: updatedParticipants
                          });
                          
                          updateData({ quote: updatedQuote });
                          return;
                        }
                        
                        // Validate numerical input
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue) && numValue >= 0) {
                          // Update both participant sumInsured and quote-level sumInsured
                          const updatedParticipants = data.quote?.participants?.map(p => 
                            p.role === 'POLICYHOLDER' ? { ...p, sumInsured: numValue } : p
                          ) || [];
                          
                          // Update only the specific fields that changed
                          const updatedQuote = createCompleteQuote(data, {
                            participants: updatedParticipants
                          });
                          
                          updateData({ quote: updatedQuote });
                        }
                      }}
                      error={errors.find(e => e.includes('sum insured') || e.includes('Sum insured'))}
                    />
                    {(() => {
                      const config = data.quote?.product?.productConfig as unknown as Record<string, unknown>;
                      const actualConfig = (config?.data || config) as Record<string, unknown> | undefined;
                      const coverageConfig = actualConfig?.coverageConfig as Record<string, unknown> | undefined;
                      const sumInsuredLimits = coverageConfig?.sumInsuredLimits as { min?: number; max?: number } | undefined;
                      if (sumInsuredLimits?.min !== undefined || sumInsuredLimits?.max !== undefined) {
                        return (
                          <p className="text-xs text-gray-500">
                            Allowed range: ${sumInsuredLimits.min?.toLocaleString() ?? '0'} &ndash; ${sumInsuredLimits.max?.toLocaleString() ?? 'unlimited'}
                          </p>
                        );
                      }
                      return (
                        <p className="text-xs text-gray-500">
                          Enter the total amount to be insured for this individual policy
                        </p>
                      );
                    })()}
                  </div>

                  {/* Hectares/Animals Insured */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Hectares/Animals Insured
                    </label>
                    <TextInput
                      type="number"
                      placeholder="Enter hectares insured (crop) or animals insured (livestock)"
                      value={getCurrentHectaresAnimalCount() != null ? getCurrentHectaresAnimalCount()!.toString() : ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value;
                        
                        // Allow empty input - set to undefined to avoid validation error
                        if (value === '') {
                          const updatedParticipants = data.quote?.participants?.map(p => 
                            p.role === 'POLICYHOLDER' ? { ...p, hectaresAnimalCount: undefined } : p
                          ) || [];
                          
                          // Update only the specific fields that changed
                          const updatedQuote = createCompleteQuote(data, {
                            participants: updatedParticipants
                          });
                          
                          updateData({ quote: updatedQuote });
                          return;
                        }
                        
                        // Validate integer input
                        const intValue = parseInt(value, 10);
                        if (!isNaN(intValue) && intValue >= 0) {
                          // Update both participant hectaresAnimalCount and quote-level hectaresAnimalCount
                          const updatedParticipants = data.quote?.participants?.map(p => 
                            p.role === 'POLICYHOLDER' ? { ...p, hectaresAnimalCount: intValue } : p
                          ) || [];
                          
                          // Update only the specific fields that changed
                          const updatedQuote = createCompleteQuote(data, {
                            participants: updatedParticipants
                          });
                          
                          updateData({ quote: updatedQuote });
                        }
                      }}
                      error={errors.find(e => e.includes('hectares') || e.includes('animals insured') || e.includes('Hectares'))}
                    />
                    <p className="text-xs text-gray-500">
                      Enter the hectares insured (for crop insurance) or animals insured (for livestock insurance)
                    </p>
                  </div>
                </div>
              )}

              {/* Add New Farmer Option */}
              <div className="border-t border-gray-200 pt-4">
                <Button variant="outline" size="sm" disabled>
                  <UsersIcon className="h-4 w-4 mr-2" />
                  Add New Farmer (Coming Soon)
                </Button>
                <p className="text-xs text-gray-500 mt-1">
                  Option to add new farmer details will be available in the next update
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <h5 className="font-medium text-gray-900">Select Organization (Master Policy)</h5>
              <p className="text-sm text-gray-600">
                Choose the organization that will hold the master policy. Farmers will be added via bulk upload.
              </p>

              <Select
                label="Organization *"
                placeholder="Select an organization..."
                options={organizations.map(org => ({
                  value: org.id,
                  label: `${org.name} (${org.code})`,
                }))}
                value={getSelectedOrganization()?.id || ''}
                onValueChange={handleOrganizationSelection}
                error={organizationError}
              />

              {/* Selected Organization Details */}
              {selectedOrganization && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircleIcon className="h-4 w-4 text-green-600" />
                        <h6 className="font-medium text-green-900">Selected Organization</h6>
                      </div>
                      <div className="text-sm text-green-800">
                        <p><strong>Name:</strong> {selectedOrganization.name}</p>
                        <p><strong>Code:</strong> {selectedOrganization.code}</p>
                        <p><strong>Type:</strong> {selectedOrganization.type}</p>
                      </div>
                    </div>
                    
                    {/* Bulk Upload Button */}
                    {data.quote?.id && (
                      <div className="flex flex-col items-end gap-2">
                        <Button
                          variant="default"
                          size="default"
                          onClick={() => setIsUploadModalOpen(true)}
                          className="flex items-center gap-2 px-6 py-3 text-base font-semibold"
                        >
                          <UploadIcon className="h-5 w-5" />
                          Upload CSV
                        </Button>
                        <p className="text-xs text-gray-600">Upload CSV file with participants</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Participant Statistics (Master Policy Only) */}
              {selectedOrganization && getPolicyType() === 'master' && data.quote?.participants && data.quote.participants.filter(p => p.role === 'INSURED').length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h6 className="font-medium text-gray-900">Participant Statistics</h6>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            const participants = data.quote!.participants.filter(p => p.role === 'INSURED' && p.farmer);
                            
                            if (participants.length === 0) {
                              toast.error('No participants to download');
                              return;
                            }

                            // Create CSV matching template format
                            const headers = ['Name', 'Phone', 'Email', 'National ID Number', 'Country', 'Province', 'District', 'Ward', 'Village', 'Latitude', 'Longitude', 'Farmer Address', 'Primary Activity/Product', 'Farm Area', 'Herd Size', 'Payment Details', 'Sum Insured', 'Hectares/Animals Insured'];
                            
                            // Resolve all administrative levels to names
                            const rowsData = await Promise.all(participants.map(async p => {
                              const f = p.farmer!;
                              const adminLevels = f.administrativeLevels && typeof f.administrativeLevels === 'object' && 'data' in f.administrativeLevels
                                ? (f.administrativeLevels as Record<string, unknown>).data as Record<string, string>
                                : f.administrativeLevels as Record<string, string> | undefined;
                              
                              // Resolve location codes to names
                              const resolvedLevels = await resolveAdminLevelNames(adminLevels);
                              
                              // Get country name
                              const countryConfig = getCountryConfig(f.countryCode || 'ZW');
                              const countryName = countryConfig?.name || f.countryCode || 'ZW';
                              
                              // Extract payment details from schema-wrapped format
                              let paymentDetailsJson = '';
                              if (f.paymentDetails) {
                                const unwrappedPayments = typeof f.paymentDetails === 'object' && 'data' in f.paymentDetails
                                  ? (f.paymentDetails as Record<string, unknown>).data
                                  : f.paymentDetails;
                                
                                // Take first payment method if array
                                const firstPayment = Array.isArray(unwrappedPayments) && unwrappedPayments.length > 0
                                  ? unwrappedPayments[0]
                                  : unwrappedPayments;
                                
                                if (firstPayment) {
                                  paymentDetailsJson = JSON.stringify(firstPayment);
                                }
                              }
                              
                              return {
                                name: f.name || '',
                                phone: f.phone || '',
                                email: f.email || '',
                                nationalId: f.nationalId || '',
                                country: countryName,
                                province: resolvedLevels['1'] || '',
                                district: resolvedLevels['2'] || '',
                                ward: resolvedLevels['3'] || '',
                                village: resolvedLevels['4'] || '',
                                latitude: f.latitude?.toString() || '',
                                longitude: f.longitude?.toString() || '',
                                address: '', // Farmer Address (JSON field - leave empty)
                                primaryActivity: f.primaryActivity || '',
                                farmArea: f.area?.toString() || '',
                                herdSize: f.herdSize?.toString() || '',
                                paymentDetails: paymentDetailsJson,
                                sumInsured: p.sumInsured?.toString() || '',
                                hectaresAnimals: p.hectaresAnimalCount?.toString() || ''
                              };
                            }));
                            
                            const escapeCsv = (val: string) => {
                              if (!val) return '';
                              // Escape quotes and wrap if contains special chars
                              if (val.includes(',') || val.includes('"') || val.includes('\n')) {
                                return `"${val.replace(/"/g, '""')}"`;
                              }
                              return val;
                            };
                            
                            const rows = rowsData.map(row => [
                              escapeCsv(row.name),
                              row.phone ? `="${row.phone}"` : '', // Excel-compatible format
                              escapeCsv(row.email),
                              escapeCsv(row.nationalId),
                              escapeCsv(row.country),
                              escapeCsv(row.province),
                              escapeCsv(row.district),
                              escapeCsv(row.ward),
                              escapeCsv(row.village),
                              escapeCsv(row.latitude),
                              escapeCsv(row.longitude),
                              escapeCsv(row.address),
                              escapeCsv(row.primaryActivity),
                              escapeCsv(row.farmArea),
                              escapeCsv(row.herdSize),
                              escapeCsv(row.paymentDetails),
                              escapeCsv(row.sumInsured),
                              escapeCsv(row.hectaresAnimals)
                            ].join(','));
                            
                            const csv = [headers.join(','), ...rows].join('\n');
                            const blob = new Blob([csv], { type: 'text/csv' });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `participants_${data.quote!.quoteNumber || 'export'}.csv`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            window.URL.revokeObjectURL(url);
                            
                            toast.success('CSV downloaded');
                          } catch (error) {
                            console.error('Error downloading CSV:', error);
                            toast.error('Failed to download CSV');
                          }
                        }}
                      >
                        Download CSV
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            const participants = data.quote!.participants.filter(p => p.role === 'INSURED' && p.farmer);
                            
                            if (participants.length === 0) {
                              toast.error('No participants to download');
                              return;
                            }

                            // Resolve all administrative levels to names
                            const participantData = await Promise.all(participants.map(async p => {
                              const f = p.farmer!;
                              const adminLevels = f.administrativeLevels && typeof f.administrativeLevels === 'object' && 'data' in f.administrativeLevels
                                ? (f.administrativeLevels as Record<string, unknown>).data as Record<string, string>
                                : f.administrativeLevels as Record<string, string> | undefined;
                              
                              // Resolve location codes to names
                              const resolvedLevels = await resolveAdminLevelNames(adminLevels);
                              
                              // Get country name
                              const countryConfig = getCountryConfig(f.countryCode || 'ZW');
                              const countryName = countryConfig?.name || f.countryCode || 'ZW';
                              
                              // Extract payment method summary
                              let paymentSummary = '';
                              if (f.paymentDetails) {
                                const unwrappedPayments = typeof f.paymentDetails === 'object' && 'data' in f.paymentDetails
                                  ? (f.paymentDetails as Record<string, unknown>).data as unknown[]
                                  : f.paymentDetails as unknown[];
                                
                                if (Array.isArray(unwrappedPayments) && unwrappedPayments.length > 0) {
                                  const firstPayment = unwrappedPayments[0] as Record<string, unknown>;
                                  const paymentType = firstPayment.type as string;
                                  if (paymentType === 'mobile_money') {
                                    paymentSummary = `${(firstPayment.provider as string) || 'Mobile Money'}: ${(firstPayment.registered_mobile_number as string) || ''}`;
                                  } else if (paymentType === 'bank_transfer') {
                                    paymentSummary = `Bank: ${(firstPayment.bank_name as string) || ''} - ${(firstPayment.account_number as string) || ''}`;
                                  }
                                }
                              }
                              
                              return {
                                farmer: f,
                                participant: p,
                                country: countryName,
                                province: resolvedLevels['1'] || '',
                                district: resolvedLevels['2'] || '',
                                ward: resolvedLevels['3'] || '',
                                village: resolvedLevels['4'] || '',
                                paymentSummary
                              };
                            }));

                            // Create HTML with printable table
                            const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Participants - ${data.quote!.quoteNumber || 'Export'}</title>
  <style>
    @media print {
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
    }
    body { font-family: Arial, sans-serif; margin: 20px; }
    h1 { color: #1f2937; margin-bottom: 10px; }
    .info { color: #6b7280; font-size: 14px; margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; }
    th { background-color: #f3f4f6; padding: 12px 8px; text-align: left; border: 1px solid #e5e7eb; font-size: 12px; position: sticky; top: 0; }
    td { padding: 8px; border: 1px solid #e5e7eb; font-size: 12px; }
    tr:nth-child(even) { background-color: #f9fafb; }
    .number { text-align: right; }
  </style>
</head>
<body>
  <h1>Participant List - ${data.quote!.quoteNumber || 'Export'}</h1>
  <div class="info">Generated: ${new Date().toLocaleDateString()} | Total Participants: ${participants.length}</div>
  <table>
    <thead>
      <tr>
        <th>Name</th>
        <th>Phone</th>
        <th>Email</th>
        <th>Country</th>
        <th>Province</th>
        <th>District</th>
        <th>Ward</th>
        <th>Village</th>
        <th>Latitude</th>
        <th>Longitude</th>
        <th>Primary Activity</th>
        <th class="number">Farm Area</th>
        <th>Payment Method</th>
        <th class="number">Sum Insured</th>
        <th class="number">Hectares/Animals</th>
      </tr>
    </thead>
    <tbody>
      ${participantData.map(({ farmer: f, participant: p, country, province, district, ward, village, paymentSummary }) => {
        return `<tr>
          <td>${f.name || ''}</td>
          <td>${f.phone || ''}</td>
          <td>${f.email || ''}</td>
          <td>${country}</td>
          <td>${province}</td>
          <td>${district}</td>
          <td>${ward}</td>
          <td>${village}</td>
          <td>${f.latitude || ''}</td>
          <td>${f.longitude || ''}</td>
          <td>${f.primaryActivity || ''}</td>
          <td class="number">${f.area || ''}</td>
          <td>${paymentSummary}</td>
          <td class="number">$${p.sumInsured?.toLocaleString() || '0'}</td>
          <td class="number">${p.hectaresAnimalCount?.toLocaleString() || '0'}</td>
        </tr>`;
      }).join('')}
    </tbody>
  </table>
</body>
</html>`;
                            
                            const blob = new Blob([html], { type: 'text/html' });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `participants_${data.quote!.quoteNumber || 'export'}.html`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            window.URL.revokeObjectURL(url);
                            
                            toast.success('HTML downloaded');
                          } catch (error) {
                            console.error('Error downloading HTML:', error);
                            toast.error('Failed to download HTML');
                          }
                        }}
                      >
                        Download HTML
                      </Button>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Last Upload</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        <tr>
                          <td className="px-3 py-2 text-gray-900">Participants</td>
                          <td className="px-3 py-2 text-right font-medium">
                            {data.quote.participants.filter(p => p.role === 'INSURED').length}
                          </td>
                          <td className="px-3 py-2 text-right font-medium text-green-600">
                            {data.bulkUploadData?.validRecords || 0}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2 text-gray-900">Sum Insured</td>
                          <td className="px-3 py-2 text-right font-medium">
                            ${data.quote.participants
                              .filter(p => p.role === 'INSURED')
                              .reduce((sum, p) => sum + (Number(p.sumInsured) || 0), 0)
                              .toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-right font-medium text-green-600">
                            {data.bulkUploadData?.validRecords ? 
                              `$${(data.quote.participants
                                .filter(p => p.role === 'INSURED')
                                .reduce((sum, p) => sum + (Number(p.sumInsured) || 0), 0) / 
                                data.quote.participants.filter(p => p.role === 'INSURED').length * 
                                data.bulkUploadData.validRecords).toLocaleString(undefined, {maximumFractionDigits: 0})}` 
                              : '$0'}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2 text-gray-900">Hectares/Animals</td>
                          <td className="px-3 py-2 text-right font-medium">
                            {data.quote.participants
                              .filter(p => p.role === 'INSURED')
                              .reduce((sum, p) => sum + (Number(p.hectaresAnimalCount) || 0), 0)
                              .toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-right font-medium text-green-600">
                            {data.bulkUploadData?.validRecords ? 
                              (data.quote.participants
                                .filter(p => p.role === 'INSURED')
                                .reduce((sum, p) => sum + (Number(p.hectaresAnimalCount) || 0), 0) / 
                                data.quote.participants.filter(p => p.role === 'INSURED').length * 
                                data.bulkUploadData.validRecords).toLocaleString(undefined, {maximumFractionDigits: 0}) 
                              : '0'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Note: Removed policy type summary card - validation now handled by standard error card */}

      {/* Proof of Stake Section */}
      {((getPolicyType() === 'individual' && getPolicyholder()?.farmer) || (getPolicyType() === 'master' && getSelectedOrganization())) && (
        <div className="border border-gray-200 rounded-lg p-4">
          <h6 className="font-medium text-gray-900 mb-3">Proof of Stake</h6>
          
          <div className={`border rounded-lg p-3 ${proofConfirmed ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className="flex gap-2">
              <InfoIcon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${proofConfirmed ? 'text-green-600' : 'text-yellow-600'}`} />
              <div className={`text-sm ${proofConfirmed ? 'text-green-800' : 'text-yellow-800'}`}>
                <p className="font-medium">
                  {proofConfirmed ? 'Proof of Insurable Interest Confirmed' : 'Proof of Insurable Interest'}
                </p>
                <p>
                  {proofConfirmed 
                    ? 'The proof of insurable interest has been confirmed for this quote.'
                    : 'Please confirm that sufficient proof of insurable interest has been provided for this policy'}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-3 flex gap-2">
            <Button 
              variant={proofConfirmed ? "default" : "outline"} 
              size="sm"
              onClick={() => {
                const newState = !proofConfirmed;
                setProofConfirmed(newState);
                updateData({ proofOfInterestConfirmed: newState });
              }}
            >
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              {proofConfirmed ? 'Proof of insurable interest confirmed' : 'Confirm Proof of insurable interest'}
            </Button>
            
            <Button 
              variant="outline" 
              size="sm" 
              disabled={uploadingProof}
              onClick={async () => {
                // Create file input and trigger click
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'application/pdf,.pdf';
                input.onchange = async (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (!file) return;
                  
                  try {
                    setUploadingProof(true);
                    const farmerId = getPolicyholder()?.farmer?.id;
                    
                    // For master policies, farmerId is optional - document can be stored at quote level
                    if (getPolicyType() === 'individual' && !farmerId) {
                      toast.error('No farmer selected');
                      return;
                    }
                    
                    // Upload document
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('type', 'proof-of-stake');
                    if (farmerId) {
                      formData.append('farmerId', farmerId);
                    }
                    
                    const response = await fetch('/api/farmers/documents/upload', {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('auth-token')}`
                      },
                      body: formData
                    });
                    
                    if (!response.ok) {
                      throw new Error('Failed to upload document');
                    }
                    
                    const result = await response.json();
                    
                    // Add document to local state and wizard data
                    if (result.data?.document) {
                      const doc = result.data.document;
                      const newDoc = {
                        id: doc.id,
                        filename: doc.filename,
                        url: doc.url,
                        size: doc.size,
                        mimeType: doc.mimeType
                      };
                      const updatedDocs = [...uploadedDocuments, newDoc];
                      setUploadedDocuments(updatedDocs);
                      
                      // Save to wizard data (don't auto-confirm - user must click button)
                      updateData({ 
                        proofOfInterestDocuments: updatedDocs
                        // Note: proofOfInterestConfirmed is NOT set here - user must manually confirm
                      });
                    }
                    
                    toast.success('Document uploaded successfully');
                    // Don't auto-set confirmation - user must click the confirm button
                  } catch (error) {
                    console.error('Error uploading document:', error);
                    toast.error('Failed to upload document');
                  } finally {
                    setUploadingProof(false);
                  }
                };
                input.click();
              }}
            >
              <UploadIcon className="h-4 w-4 mr-2" />
              {uploadingProof ? 'Uploading...' : 'Upload New Document'}
            </Button>
          </div>

          {/* Show uploaded documents */}
          {uploadedDocuments.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-sm font-medium text-gray-700">Uploaded Documents:</p>
              {uploadedDocuments.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <span className="text-sm text-gray-900">{doc.filename}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPreviewDoc({
                        url: doc.url,
                        filename: doc.filename,
                        size: doc.size,
                        mimeType: doc.mimeType
                      })}
                      className="text-blue-600 hover:text-blue-700 p-1 rounded hover:bg-blue-50"
                      title="View document"
                    >
                      <EyeIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete ${doc.filename}?`)) {
                          const updatedDocs = uploadedDocuments.filter(d => d.id !== doc.id);
                          setUploadedDocuments(updatedDocs);
                          // Update wizard data
                          updateData({ proofOfInterestDocuments: updatedDocs });
                          toast.success('Document removed from quote');
                        }
                      }}
                      className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50"
                      title="Remove document"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Note: Removed duplicate error display - now handled by SimpleWizardShell */}

      {/* Bulk Upload Modal */}
      {isUploadModalOpen && data.quote?.id && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Bulk Upload Participants</h3>
              <button
                onClick={() => setIsUploadModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <BulkUploadModal
              config={{
                context: 'quote',
                quoteId: data.quote.id,
                uploadEndpoint: `/api/quotes/${data.quote.id}/bulk-upload`,
                validateEndpoint: (uploadId) => `/api/quotes/bulk-upload/${uploadId}/validate`,
                decisionEndpoint: (uploadId) => `/api/quotes/bulk-upload/${uploadId}/user-decision`,
                downloadTemplate: async () => {
                  try {
                  const { blob, filename } = await apiClient.downloadQuoteBulkUploadTemplate();
                    
                    // Create download link and trigger download
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = filename || 'quote_bulk_upload_template.csv';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error('Error downloading bulk upload template:', error);
                    throw error;
                  }
                },
                entityName: 'Participants',
              }}
              onUploadComplete={async (validationResults) => {
                // Map ValidationResults to bulkUploadData format
                // Use timestamp as uploadId to track last upload
                const uploadTimestamp = Date.now().toString();
                const uploadData = {
                  uploadId: uploadTimestamp,
                  status: 'completed',
                  validRecords: validationResults.valid_count,
                  invalidRecords: validationResults.invalid_count,
                };
                
                // Store upload metadata (but don't trigger quote update yet)
                updateData({ bulkUploadData: uploadData });
                
                // Wait a bit for processing to complete and commit
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // Fetch the uploaded participants from backend
                try {
                  const response = await quoteService.getQuoteById(data.quote!.id);
                  const quote = response.quote;
                  
                  if (quote.participants && quote.participants.length > 0) {
                    // Enrich organization participants with full organization data from local state
                    const enrichedParticipants = quote.participants.map(participant => {
                      const enriched = {
                        ...participant,
                        sumInsured: participant.sumInsured ? Number(participant.sumInsured) : undefined, // Ensure number type
                        hectaresAnimalCount: participant.hectaresAnimalCount !== undefined ? Number(participant.hectaresAnimalCount) : undefined // Ensure number type
                      };
                      
                      if (participant.role === 'POLICYHOLDER' && participant.organizationId) {
                        const orgData = organizations.find(org => org.id === participant.organizationId);
                        enriched.organization = orgData;
                      }
                      
                      return enriched;
                    });
                    
                    // Update quote object with all participants from backend
                    updateData({ 
                      quote: createCompleteQuote(data, {
                        participants: enrichedParticipants
                      }),
                      bulkUploadData: {
                        ...uploadData,
                        status: 'completed'
                      }
                    });
                  }
                  
                  setIsUploadModalOpen(false);
                } catch (error) {
                  console.error('Error fetching participants after bulk upload:', error);
                }
              }}
              onError={(error) => {
                console.error('Bulk upload error:', error);
              }}
            />
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {previewDoc && (
        <DocumentPreviewModal
          isOpen={!!previewDoc}
          onClose={() => setPreviewDoc(null)}
          documentUrl={previewDoc.url}
          fileName={previewDoc.filename}
          fileSize={previewDoc.size}
          mimeType={previewDoc.mimeType}
        />
      )}
    </div>
  );
}
